import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '@/i18n/context';
import { Button } from '@/components/ui/button';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeToggle from './ThemeToggle';
import { Menu, X, ShoppingCart } from 'lucide-react';
import logoSymbol from '@/assets/logo-symbol.svg';
import { useCart } from '@/context/CartContext';

import { useNavigate } from 'react-router-dom';

const Header = () => {
  const { t, routes } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { toggleDrawer, itemCount } = useCart();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const navItems = [
    { label: t.nav.inicio, to: routes.home },
    { label: t.nav.sobre, to: routes.about },
    { label: t.nav.coaching, to: routes.coaching },
    { label: t.nav.libros, to: routes.resources },
    { label: t.nav.blog, to: routes.blog },
    { label: t.nav.entrevistas, to: routes.interviews },
    { label: t.nav.contacto, to: routes.contact },
  ];

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 bg-card backdrop-blur-xl shadow-[0_1px_3px_hsl(var(--navy)/0.06)] border-b border-border/50`}>
      <div className="container-wide flex items-center justify-between h-[4.25rem]">
        <Link to={routes.home} className="flex items-center hover:opacity-80 transition-opacity duration-200">
          <img src={logoSymbol} alt="Andres Luna" className="h-9 w-auto" width="36" height="36" />
        </Link>
        <nav className="hidden xl:flex items-center gap-0.5">
          {navItems.map((item) => (
            <Link key={item.to} to={item.to} className={`relative px-3 py-2 text-[13px] font-medium tracking-wide rounded-lg transition-all duration-200 ${location.pathname === item.to ? 'text-primary' : 'text-foreground/80 hover:text-foreground hover:bg-muted/40'}`}>
              {item.label}
              {location.pathname === item.to && <span className="absolute bottom-0.5 left-3 right-3 h-[2px] bg-primary rounded-full" />}
            </Link>
          ))}
        </nav>
        <div className="hidden xl:flex items-center gap-3">
          <ThemeToggle />
          <LanguageSwitcher />
          <button onClick={() => toggleDrawer()} className="relative p-2 text-foreground/65 hover:text-foreground transition-colors rounded-lg hover:bg-muted/40" aria-label="Cart">
            <ShoppingCart size={18} />
            {itemCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{itemCount}</span>
            )}
          </button>
          <Button variant="navCta" size="sm" onClick={() => navigate(routes.contact)}>
            {t.nav.cta}
          </Button>
        </div>
        <div className="flex xl:hidden items-center gap-2">
          <ThemeToggle />
          <LanguageSwitcher />
          <button onClick={() => toggleDrawer()} className="relative p-2 text-foreground/70 hover:text-foreground transition-colors rounded-lg hover:bg-muted/40" aria-label="Cart">
            <ShoppingCart size={18} />
            {itemCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{itemCount}</span>
            )}
          </button>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 text-foreground/70 hover:text-foreground transition-colors rounded-lg hover:bg-muted/40" aria-label="Menu">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <div className={`xl:hidden overflow-hidden transition-all duration-300 ease-out ${mobileOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <nav className="container-wide py-4 pb-6 flex flex-col gap-0.5 border-t border-border/40">
          {navItems.map((item) => (
            <Link key={item.to} to={item.to} onClick={() => setMobileOpen(false)} className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-150 ${location.pathname === item.to ? 'text-primary bg-green-soft' : 'text-foreground/65 hover:text-foreground hover:bg-muted/50'}`}>{item.label}</Link>
          ))}
          <div className="pt-3 px-1">
            <Button variant="cta" size="lg" className="w-full" onClick={() => { setMobileOpen(false); navigate(routes.contact); }}>
              {t.nav.cta}
            </Button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
